import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldAlert, Plus, Trash2, Sparkles, Loader2, Filter, Save, X, Download,
} from "lucide-react";

const LEVEL_COLORS = {
  low:          { badge: "border-emerald-400/40 bg-emerald-500/10 text-emerald-200", fill: "bg-emerald-600/70" },
  medium:       { badge: "border-amber-400/50 bg-amber-500/10 text-amber-200",       fill: "bg-amber-500/70" },
  high:         { badge: "border-orange-400/50 bg-orange-500/10 text-orange-200",    fill: "bg-orange-500/80" },
  unacceptable: { badge: "border-rose-400/60 bg-rose-500/10 text-rose-200",          fill: "bg-rose-500/80" },
};

const cellClass = (sev, prob) => {
  const score = sev * prob;
  const lvl = score >= 16 ? "unacceptable" : score >= 9 ? "high" : score >= 4 ? "medium" : "low";
  return { lvl, cls: LEVEL_COLORS[lvl].fill };
};

export default function RiskMatrixPanel({ projectId, project }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [showMitigated, setShowMitigated] = useState(true);

  const load = async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/risk/items`, { params: { project_id: projectId } });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load risk items");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId]);

  const grid = useMemo(() => {
    const g = {};
    items.forEach((it) => {
      const rs = it.residual_severity || it.severity;
      const rp = it.residual_probability || it.probability;
      const key = `${rs}-${rp}`;
      (g[key] = g[key] || []).push(it);
    });
    return g;
  }, [items]);

  const create = async (draft) => {
    try {
      await api.post(`/risk/items`, { ...draft, project_id: projectId });
      toast.success("Risk added");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add");
    }
  };

  const update = async (id, patch) => {
    try {
      await api.put(`/risk/items/${id}`, patch);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this risk item?")) return;
    try {
      await api.delete(`/risk/items/${id}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const suggest = async () => {
    if (!project?.device_name || !project?.description) {
      toast.error("Project needs a device name and description before hazards can be suggested.");
      return;
    }
    setSuggesting(true);
    try {
      const { data } = await api.post(`/risk/suggest`, {
        project_id: projectId,
        device_name: project.device_name,
        intended_use: project.description || project.intended_use || "",
        key_features: project.key_features || "",
        existing_hazards: items.map((it) => it.hazard),
      });
      // Auto-create each suggestion
      for (const s of data.suggestions || []) {
        await api.post(`/risk/items`, { ...s, project_id: projectId });
      }
      toast.success(`Added ${data.suggestions.length} risk items`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI suggestion failed");
    } finally {
      setSuggesting(false);
    }
  };

  const exportCsv = () => {
    const rows = [
      ["id","hazard","hazardous_situation","harm","severity","probability","mitigation","residual_severity","residual_probability","iso_14971_ref","status","owner"],
      ...items.map((it) => [
        it.id, it.hazard, it.hazardous_situation, it.harm,
        it.severity, it.probability, it.mitigation,
        it.residual_severity ?? "", it.residual_probability ?? "",
        it.iso_14971_ref, it.status, it.owner,
      ].map((v) => `"${String(v ?? "").replace(/"/g,'""')}"`).join(",")),
    ];
    const csv = rows.join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `risk_matrix_${projectId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!projectId) {
    return (
      <div className="flex h-64 flex-col items-center justify-center border border-dashed border-slate-200 bg-white text-center text-slate-500">
        <ShieldAlert className="mb-3 h-6 w-6 text-[#2563EB]" strokeWidth={1.5} />
        <div className="text-sm">Pick a project first.</div>
        <div className="mt-1 text-xs text-slate-400">Risk matrices live inside a project.</div>
      </div>
    );
  }

  return (
    <div data-testid="risk-matrix-root" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="overline mb-1 text-[#2563EB]">RISK MANAGEMENT · ISO 14971</div>
          <h2 className="font-serif text-2xl font-semibold text-slate-900">
            {project?.device_name || "Risk matrix"}
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Hazards → hazardous situations → harms → mitigations → residual risk. {items.length} item{items.length === 1 ? "" : "s"}.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="mr-1 flex items-center gap-1.5 text-[11px] text-slate-500">
            <input type="checkbox" checked={showMitigated} onChange={(e) => setShowMitigated(e.target.checked)} className="accent-[#2563EB]" />
            show mitigated
          </label>
          <button data-testid="risk-suggest-btn" onClick={suggest} disabled={suggesting} className="inline-flex items-center gap-1.5 border border-[#2563EB]/50 bg-[#2563EB]/10 px-3 py-1.5 text-xs font-medium text-[#93C5FD] hover:border-[#2563EB] hover:bg-[#2563EB]/20 disabled:opacity-40">
            {suggesting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            Suggest hazards
          </button>
          <button data-testid="risk-add-btn" onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 bg-[#2563EB] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#1D4ED8]">
            <Plus className="h-3.5 w-3.5" /> Add hazard
          </button>
          <button onClick={exportCsv} disabled={items.length === 0} className="inline-flex items-center gap-1.5 border border-slate-200 px-3 py-1.5 text-xs text-slate-600 hover:border-slate-300 hover:text-slate-900 disabled:opacity-30">
            <Download className="h-3.5 w-3.5" /> CSV
          </button>
        </div>
      </div>

      {/* 5x5 Matrix */}
      <div className="border border-slate-200 bg-white p-4">
        <div className="mb-2 overline text-slate-500">5×5 RESIDUAL RISK MATRIX · SEVERITY × PROBABILITY</div>
        <div className="grid gap-1" style={{ gridTemplateColumns: "auto repeat(5, 1fr)" }}>
          <div />
          {[1,2,3,4,5].map((p) => (
            <div key={p} className="overline pb-2 text-center text-[10px] text-slate-500">P{p}</div>
          ))}
          {[5,4,3,2,1].map((s) => (
            <React.Fragment key={s}>
              <div className="flex items-center justify-end pr-2 text-[10px] uppercase text-slate-500">S{s}</div>
              {[1,2,3,4,5].map((p) => {
                const key = `${s}-${p}`;
                const cell = grid[key] || [];
                const { cls } = cellClass(s, p);
                return (
                  <div
                    key={key}
                    data-testid={`risk-cell-${s}-${p}`}
                    className={`relative flex min-h-[72px] flex-col justify-between border border-slate-100 p-2 ${cls}`}
                  >
                    <div className="text-[10px] text-slate-600">S{s}·P{p} · {s*p}</div>
                    <div className="flex flex-wrap gap-1">
                      {cell.slice(0, 4).map((it) => (
                        <button
                          key={it.id}
                          onClick={() => setEditing(it)}
                          title={it.hazard}
                          className="border border-slate-300 bg-slate-900/40 px-1.5 py-0.5 text-[10px] font-medium text-white hover:bg-slate-900/60"
                        >
                          {(it.hazard || "").slice(0, 22)}{(it.hazard || "").length > 22 ? "…" : ""}
                        </button>
                      ))}
                      {cell.length > 4 && <span className="text-[10px] text-slate-700">+{cell.length - 4}</span>}
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
        <div className="mt-3 flex items-center gap-4 text-[10px] uppercase text-slate-500">
          {Object.entries(LEVEL_COLORS).map(([k, v]) => (
            <div key={k} className="flex items-center gap-1.5">
              <span className={`h-3 w-3 ${v.fill}`} />
              {k}
            </div>
          ))}
        </div>
      </div>

      {/* Table */}
      <div data-testid="risk-table" className="border border-slate-200 bg-white">
        <div className="border-b border-slate-200 bg-[#FAFAF7] px-4 py-2">
          <div className="overline text-slate-500">HAZARDS</div>
        </div>
        {items.length === 0 && !loading && (
          <div className="p-8 text-center text-sm text-slate-500">
            No risks yet. Click <b className="text-slate-900">Suggest hazards</b> for an AI starter or <b className="text-slate-900">Add hazard</b> to enter one manually.
          </div>
        )}
        <div className="divide-y divide-slate-200">
          {items
            .filter((it) => showMitigated || it.status !== "mitigated")
            .map((it) => {
              const initial = LEVEL_COLORS[it.initial_level] || LEVEL_COLORS.medium;
              const residual = LEVEL_COLORS[it.residual_level] || LEVEL_COLORS.medium;
              return (
                <div key={it.id} data-testid={`risk-row-${it.id}`} className="grid grid-cols-12 gap-3 px-4 py-3 text-xs">
                  <div className="col-span-12 lg:col-span-4">
                    <button onClick={() => setEditing(it)} className="text-left text-slate-900 hover:text-[#93C5FD]">
                      <div className="font-medium">{it.hazard}</div>
                      <div className="mt-0.5 text-[11px] text-slate-500">{it.hazardous_situation}</div>
                    </button>
                  </div>
                  <div className="col-span-6 lg:col-span-3 text-slate-600">{it.harm || "—"}</div>
                  <div className="col-span-3 lg:col-span-1 flex items-center gap-1">
                    <span className={`border ${initial.badge} px-1.5 py-0.5 text-[10px] font-semibold`}>{it.initial_level}</span>
                    <span className="text-[10px] text-slate-400">{it.initial_score}</span>
                  </div>
                  <div className="col-span-6 lg:col-span-2 text-slate-600">{it.mitigation || "—"}</div>
                  <div className="col-span-3 lg:col-span-1 flex items-center gap-1">
                    <span className={`border ${residual.badge} px-1.5 py-0.5 text-[10px] font-semibold`}>{it.residual_level}</span>
                    <span className="text-[10px] text-slate-400">{it.residual_score}</span>
                  </div>
                  <div className="col-span-12 lg:col-span-1 flex justify-end">
                    <button data-testid={`risk-delete-${it.id}`} onClick={() => remove(it.id)} className="p-1 text-slate-400 hover:text-rose-300">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      {(editing || creating) && (
        <RiskEditor
          item={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSave={async (patch) => {
            if (editing) await update(editing.id, patch);
            else await create(patch);
            setEditing(null); setCreating(false);
          }}
        />
      )}
    </div>
  );
}

function RiskEditor({ item, onClose, onSave }) {
  const [draft, setDraft] = useState(
    item
      ? { ...item }
      : { hazard: "", hazardous_situation: "", harm: "", severity: 3, probability: 3, mitigation: "", residual_severity: 3, residual_probability: 3, iso_14971_ref: "", status: "open", owner: "", verification: "" }
  );
  const num = (k, v) => setDraft({ ...draft, [k]: Math.max(1, Math.min(5, parseInt(v || "1"))) });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4">
      <div className="w-full max-w-3xl border border-slate-200 bg-[#FAFAF7]">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <div className="font-serif text-lg font-semibold text-slate-900">
            {item ? "Edit risk" : "Add a risk"}
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"><X className="h-5 w-5" /></button>
        </div>
        <div className="grid gap-3 p-6 md:grid-cols-2">
          <Row label="Hazard" full>
            <input data-testid="risk-input-hazard" value={draft.hazard} onChange={(e) => setDraft({ ...draft, hazard: e.target.value })} className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" />
          </Row>
          <Row label="Hazardous situation" full>
            <textarea value={draft.hazardous_situation} onChange={(e) => setDraft({ ...draft, hazardous_situation: e.target.value })} rows={2} className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" />
          </Row>
          <Row label="Harm">
            <input value={draft.harm} onChange={(e) => setDraft({ ...draft, harm: e.target.value })} className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" />
          </Row>
          <Row label="ISO 14971 clause">
            <input value={draft.iso_14971_ref} onChange={(e) => setDraft({ ...draft, iso_14971_ref: e.target.value })} placeholder="§5.4 hazard identification" className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" />
          </Row>
          <Row label="Initial severity (1-5)">
            <input type="number" min={1} max={5} value={draft.severity} onChange={(e) => num("severity", e.target.value)} className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" />
          </Row>
          <Row label="Initial probability (1-5)">
            <input type="number" min={1} max={5} value={draft.probability} onChange={(e) => num("probability", e.target.value)} className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" />
          </Row>
          <Row label="Mitigation" full>
            <textarea value={draft.mitigation} onChange={(e) => setDraft({ ...draft, mitigation: e.target.value })} rows={2} className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" />
          </Row>
          <Row label="Residual severity">
            <input type="number" min={1} max={5} value={draft.residual_severity || draft.severity} onChange={(e) => num("residual_severity", e.target.value)} className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" />
          </Row>
          <Row label="Residual probability">
            <input type="number" min={1} max={5} value={draft.residual_probability || draft.probability} onChange={(e) => num("residual_probability", e.target.value)} className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" />
          </Row>
          <Row label="Verification / testing" full>
            <input value={draft.verification} onChange={(e) => setDraft({ ...draft, verification: e.target.value })} className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" />
          </Row>
          <Row label="Owner">
            <input value={draft.owner} onChange={(e) => setDraft({ ...draft, owner: e.target.value })} className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" />
          </Row>
          <Row label="Status">
            <select value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })} className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900">
              <option value="open">Open</option>
              <option value="mitigated">Mitigated</option>
              <option value="accepted">Accepted</option>
              <option value="closed">Closed</option>
            </select>
          </Row>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-6 py-4">
          <button onClick={onClose} className="border border-slate-200 px-4 py-2 text-xs font-medium text-slate-600 hover:border-slate-300 hover:text-slate-900">Cancel</button>
          <button data-testid="risk-save" onClick={() => onSave(draft)} disabled={!draft.hazard.trim()} className="inline-flex items-center gap-1.5 bg-[#2563EB] px-4 py-2 text-xs font-semibold text-white hover:bg-[#1D4ED8] disabled:opacity-40">
            <Save className="h-3.5 w-3.5" /> Save
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="overline mb-1 text-slate-500">{label}</div>
      {children}
    </label>
  );
}
